import React, { useMemo, useState } from 'react';
import clsx from 'clsx';
import deleteIcon from '@/assets/images/svg/delete_icon.svg';
import { AddInput } from '@/components/common/AddInput';
import { ConfiguredBadge } from './ConfiguredBadge';

interface ConfigListInputProps {
  label: string;
  description: string;
  value: string;
  onChange: (value: string) => void;
  icon?: React.ReactNode;
  placeholder?: string;
  // Badge + undo support
  isModified?: boolean;
  onUndo?: () => void;
}

export const ConfigListInput: React.FC<ConfigListInputProps> = ({
  label,
  description,
  value,
  onChange,
  icon,
  placeholder,
  isModified,
  onUndo,
}) => {
  const [inputValue, setInputValue] = useState('');

  // Convert comma-separated string to array (stable trimming + empty filter)
  const items = useMemo(
    () =>
      value
        ? value
            .split(',')
            .map((str) => str.trim())
            .filter(Boolean)
        : [],
    [value]
  );

  const handleAdd = () => {
    const val = inputValue.trim();
    if (val && !items.includes(val)) {
      const newItems = [...items, val];
      onChange(newItems.join(', '));
      setInputValue('');
    }
  };

  const handleRemove = (itemToRemove: string) => {
    const newItems = items.filter((i) => i !== itemToRemove);
    onChange(newItems.join(', '));
  };

  return (
    <div
      className={clsx(
        'relative flex w-full flex-col gap-6',
        'rounded-20 border border-brand-300 bg-gray-25 p-6'
      )}
    >
      {/* Configured Badge */}
      {isModified && onUndo && <ConfiguredBadge onUndo={onUndo} />}
      <div className='flex items-start gap-4 pr-16'>
        {icon && (
          <div className='flex size-[40px] shrink-0 items-center justify-center rounded-[13px] bg-brand-750 text-white'>
            {icon}
          </div>
        )}
        <div className='flex flex-col gap-1'>
          <h4 className='font-sans text-20 font-medium leading-27 text-dark-100'>{label}</h4>
          <p className='font-sans text-13 font-normal leading-[18px] text-gray-600'>
            {description}
          </p>
        </div>
      </div>

      <div className='grid grid-cols-1 gap-3 sm:grid-cols-2 md:pl-[64px]'>
        {/* Input spans full width */}
        <div className='col-span-1 sm:col-span-2'>
          <AddInput
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onAdd={handleAdd}
            onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
            placeholder={placeholder}
          />
        </div>

        {/* Render Items as purple cards in the same 2-column grid */}
        {items.map((item, idx) => (
          <div
            key={`${item}-${idx}`}
            className={clsx(
              'flex items-center justify-between rounded-10 px-4 py-3',
              'bg-purple-250'
            )}
          >
            <span className='font-sans text-16 font-normal leading-27 text-brand-800'>{item}</span>

            <button
              onClick={() => handleRemove(item)}
              className='flex items-center justify-center transition-opacity hover:opacity-80'
              aria-label={`Remove ${item}`}
              type='button'
            >
              <img src={deleteIcon} alt='Remove' className='size-[20px]' />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};
